import React from "react";
import PropTypes from "prop-types";
import { isUberStatusClickable } from "../../../utils/uberStatus";

/**
 * Cellule presentant le statut Uber d'une tache.
 * Affiche un bouton d'action si le statut est cliquable (canceled/pending/pickup),
 * un badge pour les autres statuts, ou "-" si pas de donnees Uber.
 *
 * @param {Object} props.task - La tache a afficher
 * @param {Object} props.dock - Le dock contenant la tache
 * @param {Function} props.onResolveClick - Callback pour ouvrir la modal de resolution
 */
const UberStatusCell = ({ task, dock, onResolveClick }) => {
  // Verifier si la tache a des external_shipping_jobs
  const externalJob = task.external_shipping_jobs && task.external_shipping_jobs[0];

  // Si pas de externalJob, afficher "-"
  if (!externalJob) {
    return <span className="text-muted">—</span>;
  }

  const status = externalJob.status;
  const isResolving = task.resolving === true;

  // Si en cours de resolution, afficher un spinner
  if (isResolving) {
    return (
      <button
        className="btn btn-xs btn-default"
        disabled
        title="Extraction en cours..."
      >
        <i className="fa-solid fa-spinner fa-spin me-1"></i>
        Extraction...
      </button>
    );
  }

  // Utiliser le predicat partage pour determiner la cliquabilite
  if (isUberStatusClickable(task)) {
    // Si le statut est "canceled", afficher le bouton d'extraction
    if (status === "canceled") {
      return (
        <button
          className="btn btn-xs btn-danger"
          onClick={() => onResolveClick(task, dock)}
          title="Cliquez pour extraire cette commande du dock"
        >
          Annulée
        </button>
      );
    }

    // Statuts "pending" ou "pickup" : bouton d'annulation + extraction
    return (
      <button
        className="btn btn-xs btn-secondary"
        onClick={() => onResolveClick(task, dock)}
        title="Annuler la livraison Uber et extraire la commande du dock"
      >
        {status}
      </button>
    );
  }

  // Pour les autres statuts, afficher un badge
  const badgeClass = getBadgeClass(status);
  return (
    <span className={`badge ${badgeClass}`}>
      {translateStatus(status)}
    </span>
  );
};

/**
 * Traduit le statut Uber en francais.
 * @param {string} status - Le statut Uber
 * @returns {string} Le statut traduit
 */
const translateStatus = (status) => {
  const translations = {
    "canceled": "Annulée",
  };
  return translations[status] || status;
};

/**
 * Retourne la classe CSS du badge selon le statut.
 * @param {string} status - Le statut Uber
 * @returns {string} La classe CSS
 */
const getBadgeClass = (status) => {
  const classes = {
    "pending": "badge-secondary",
    "pickup": "badge-secondary",
    "dropoff": "badge-info",
    "delivered": "badge-success",
    "canceled": "badge-danger",
  };
  return classes[status] || "badge-secondary";
};

UberStatusCell.propTypes = {
  task: PropTypes.object.isRequired,
  dock: PropTypes.object,
  onResolveClick: PropTypes.func,
};

UberStatusCell.defaultProps = {
  dock: null,
  onResolveClick: () => {},
};

export default UberStatusCell;